/**
 * 富文本内容展示组件
 * @description 统一处理站内信/通知正文的渲染：XSS 净化 + 图片点击预览。
 *              正文是 HTML 字符串，图片无法直接渲染为 antd Image，
 *              故用事件委托捕获 img 点击，再驱动一个隐藏的 Image 打开预览。
 * @author ritaxxu
 */
import React, { useMemo, useState } from 'react'
import { Image } from 'antd'
import parse from 'html-react-parser'
import { sanitizeHtml } from '@library/sanitize'
import './style.less'

interface RichContentProps {
  /** 富文本 HTML 内容 */
  html?: string | null
  /** 容器额外类名 */
  className?: string
}

export const RichContent: React.FC<RichContentProps> = ({ html, className }) => {
  // 当前预览的图片地址，空串表示未打开预览
  const [previewSrc, setPreviewSrc] = useState('')

  const content = useMemo(() => parse(sanitizeHtml(html)), [html])

  // 事件委托：点到正文里的 img 才打开预览，避免给每张图单独绑事件
  const handleClick = (e: React.MouseEvent<HTMLDivElement>) => {
    const target = e.target as HTMLElement
    if (target.tagName !== 'IMG') return
    const src = (target as HTMLImageElement).currentSrc || (target as HTMLImageElement).src
    if (src) {
      setPreviewSrc(src)
    }
  }

  return (
    <>
      <div
        className={className ? `component-rich-content ${className}` : 'component-rich-content'}
        onClick={handleClick}
      >
        {content}
      </div>

      {/* 隐藏的图片实例，仅用于承载 antd 的预览浮层 */}
      <Image
        style={{ display: 'none' }}
        src={previewSrc}
        preview={{
          visible: !!previewSrc,
          src: previewSrc,
          onVisibleChange: (visible) => {
            if (!visible) setPreviewSrc('')
          },
        }}
      />
    </>
  )
}

export default RichContent
